import { useMemo } from "react";
import { DrawerBody, DrawerFooter } from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { DocumentType, gql } from "__generated__/gql";
import { assertPresence } from "utils/assertions";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import EditPanelContent, {
  CancelButton,
  SaveButton,
} from "components/Products/EditPanelContent";
import { trackEvent } from "utils/segment";
import { updateCachedProductLineSkus } from "components/Products/queries";
import { UNARCHIVED_STATUSES } from "components/Products/utils";
import { getInitialSkuComponents } from "./SkuComponents/utils";
import {
  SkuInputs,
  resetSkuRelationsCache,
  useGetMutationDataFromFormData,
} from "./utils";
import SkuPanelForm, {
  FORM_ID,
  processApolloError,
  SubmitContext,
} from "./Form";

const createSkuMutation = gql(/* GraphQL */ `
  mutation CreateSku(
    $input: CreateSkuInput!
    $skuId: UUID!
    $productAttributeIds: [UUID!]!
    $dimensions: [DimensionInput!]!
    $tiHis: [TiHiInput!]!
    $skuComponents: [SkuComponentInput!]!
  ) {
    ...UpdateSkuO2O
    createSku(input: $input) {
      sku {
        id
        name
        productLine {
          id
        }
        ...SelectedSkuDetailsFields
        ...ProductsTableSku
      }
    }
    ...UpdateSkuM2M
  }
`);

type Props = {
  initialData?: Partial<SkuInputs>;
  onClose: () => void;
  onNewCancel?: () => void;
  onNewSuccess?: (data: DocumentType<typeof createSkuMutation>) => void;
};

export default function SkuPanelCreate({
  initialData: initialDataRaw,
  onClose,
  onNewCancel,
  onNewSuccess,
}: Props) {
  const initialData = useMemo<Partial<SkuInputs>>(
    () => ({
      tiHi: null,
      skuComponents: getInitialSkuComponents(),
      ...initialDataRaw,
    }),
    [initialDataRaw],
  );

  const [createSku, { loading: isCreating }] = useBrandMutation(
    createSkuMutation,
    {
      update: (cache, result) => {
        resetSkuRelationsCache(cache);

        const newSku = result.data?.createSku?.sku;

        assertPresence(newSku?.productLine?.id);

        updateCachedProductLineSkus(
          cache,
          newSku.productLine,
          UNARCHIVED_STATUSES,
          (existingSkus) => {
            if (!existingSkus) return existingSkus;
            return [...existingSkus, newSku];
          },
        );
      },
      onCompleted: (newSku) => {
        trackEvent("SKU Created");
        if (onNewSuccess) onNewSuccess(newSku);
      },
    },
  );

  const getMutationDataFromFormData = useGetMutationDataFromFormData();

  const [handleValidSubmit, { isLoading }] = useCreateValidSubmitHandler(
    async (formData: SkuInputs, context: SubmitContext) => {
      const {
        skuData,
        skuId,
        productAttributeIds,
        dimensions,
        tiHis,
        skuComponents,
      } = await getMutationDataFromFormData(formData, context, uuidv4());

      const { data: createData } = await createSku({
        variables: {
          input: { sku: { id: skuId, ...skuData } },
          skuId,
          productAttributeIds,
          dimensions,
          tiHis,
          skuComponents,
        },
      });
      const createdSku = createData?.createSku?.sku;
      assertPresence(createdSku);
      return createdSku;
    },
    {
      getSuccessToast: (result) => ({
        title: `SKU created successfully: ${result?.name}`,
      }),
      processApolloError,
    },
  );

  return (
    <EditPanelContent
      onClose={onClose}
      description="Add New SKU"
      headingPlaceholder="New SKU"
    >
      <DrawerBody>
        <SkuPanelForm
          isCreateNew
          onValidSubmit={handleValidSubmit}
          defaultValues={initialData}
        />
      </DrawerBody>

      <DrawerFooter gap={4}>
        {onNewCancel && <CancelButton onClick={onNewCancel} />}

        <SaveButton isLoading={isLoading || isCreating} form={FORM_ID}>
          Create
        </SaveButton>
      </DrawerFooter>
    </EditPanelContent>
  );
}
